import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { usePomodoroContext } from '../contexts/PomodoroContext'
import { useAuthStore } from '../stores/auth'
import { Minimize2, Maximize2, X, Pause, Play } from 'lucide-react'

export function FloatingPomodoro() {
  const context = usePomodoroContext()
  const user = useAuthStore(s => s.user)
  const [position, setPosition] = useState({ x: globalThis.innerWidth - 220, y: 80 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const floatingRef = useRef<HTMLDivElement>(null)

  const totalSeconds = context.phase === 'work' ? context.workMin * 60 : context.breakMin * 60
  const progress = totalSeconds > 0 ? ((totalSeconds - context.secondsLeft) / totalSeconds) * 100 : 0
  const minutes = Math.floor(context.secondsLeft / 60).toString().padStart(2, '0')
  const seconds = (context.secondsLeft % 60).toString().padStart(2, '0')

  const circleRadius = context.isMinimized ? 25 : 50
  const circleCircumference = 2 * Math.PI * circleRadius
  const strokeDashoffset = circleCircumference * (1 - progress / 100)

  function handleMouseDown(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('button')) return
    setIsDragging(true)
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDragging) return
      const newX = Math.max(0, Math.min(globalThis.innerWidth - 200, e.clientX - dragOffset.x))
      const newY = Math.max(0, Math.min(globalThis.innerHeight - 200, e.clientY - dragOffset.y))
      setPosition({ x: newX, y: newY })
    }

    function handleMouseUp() {
      setIsDragging(false)
    }

    if (isDragging) {
      globalThis.addEventListener('mousemove', handleMouseMove)
      globalThis.addEventListener('mouseup', handleMouseUp)
      return () => {
        globalThis.removeEventListener('mousemove', handleMouseMove)
        globalThis.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset])

  // Don't show if idle
  if (context.phase === 'idle') return null

  const content = (
    <div
      ref={floatingRef}
      className="fixed z-50 rounded-2xl border border-border bg-card shadow-2xl backdrop-blur-sm"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'all 0.2s ease',
      }}
      onMouseDown={handleMouseDown}
    >
      {context.isMinimized ? (
        // Minimized view - compact circle
        <div className="p-3 flex items-center gap-3">
          <svg width="60" height="60">
            <circle cx="30" cy="30" r={circleRadius} fill="none" stroke="var(--border)" strokeWidth="4" />
            <circle
              cx="30" cy="30" r={circleRadius} fill="none"
              stroke={context.phase === 'break' ? '#22c55e' : 'var(--primary)'}
              strokeWidth="4" strokeLinecap="round"
              strokeDasharray={circleCircumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 30 30)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
            <text x="30" y="30" textAnchor="middle" dominantBaseline="middle"
              className="fill-foreground font-mono"
              style={{ fontSize: '12px', fontFamily: 'monospace', fill: 'currentColor' }}>
              {minutes}:{seconds}
            </text>
          </svg>
          <div className="flex items-center gap-1">
            <button
              onClick={() => context.isPaused ? context.resume() : context.pause()}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
              title={context.isPaused ? 'Resume' : 'Pause'}
            >
              {context.isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button
              onClick={() => context.setIsMinimized(false)}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
              title="Expand"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        // Expanded view
        <div className="p-4 space-y-3" style={{ minWidth: '200px' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              {context.phase === 'work' ? '🎯 Focus Time' : '☕ Break Time'}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => context.isPaused ? context.resume() : context.pause()}
                className="p-1 rounded hover:bg-accent transition-colors"
                title={context.isPaused ? 'Resume' : 'Pause'}
              >
                {context.isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => context.setIsMinimized(true)}
                className="p-1 rounded hover:bg-accent transition-colors"
                title="Minimize"
              >
                <Minimize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => user && context.stopSession(user.id)}
                className="p-1 rounded hover:bg-destructive hover:text-destructive-foreground transition-colors"
                title="Stop session"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            <svg width="120" height="120">
              <circle cx="60" cy="60" r={circleRadius} fill="none" stroke="var(--border)" strokeWidth="6" />
              <circle
                cx="60" cy="60" r={circleRadius} fill="none"
                stroke={context.phase === 'break' ? '#22c55e' : 'var(--primary)'}
                strokeWidth="6" strokeLinecap="round"
                strokeDasharray={circleCircumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
              <text x="60" y="56" textAnchor="middle" dominantBaseline="middle"
                className="fill-foreground font-mono"
                style={{ fontSize: '20px', fontFamily: 'monospace', fill: 'currentColor' }}>
                {minutes}:{seconds}
              </text>
              <text x="60" y="76" textAnchor="middle"
                className="fill-muted-foreground"
                style={{ fontSize: '10px', fill: 'var(--muted-foreground)' }}>
                {context.phase === 'work' ? 'Focus' : 'Break'}
              </text>
            </svg>

            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Cycle {context.cycles + (context.phase === 'work' ? 0 : 1)}
              </p>
              {context.activeSession?.label && (
                <p className="text-xs font-medium mt-1">{context.activeSession.label}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return createPortal(content, document.body)
}
